import './MuiClassNameSetup';
import './global.types';
import { React } from 'Imports';
import { createRoot } from 'react-dom/client';
import { GeotabManager } from './integrationPartner/geotabManager';
import { Application } from './Application';
import type { ISinglePageApplicationProps, VersionInfo } from './spa.types';

// Bootstrap previously handled by `@videoplatform/react-spa` (`SPA` + `PageRoot`).
// The props PageRoot used to compute and pass to the root component are reproduced
// here from the document `<base>`, `<title>`, favicon link, and the build-time
// globals defined by webpack DefinePlugin (see builder/webpack-base.ts gitInfo).

// Injected at build time by webpack DefinePlugin.
declare const VERSION: string;
declare const COMMITHASH: string;
declare const BRANCH: string;
declare const VERSION_WITH_TAG: string;

// Default 1x1 transparent favicon (previously supplied by PageRoot).
const DEFAULT_FAVICON_HREF =
    'data:image/x-icon;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQEAYAAABPYyMiAAAABmJLR0T///////8JWPfcAAAACXBIWXMAAABIAAAASABGyWs+AAAAF0lEQVRIx2NgGAWjYBSMglEwCkbBSAcACBAAAeaR9cIAAAAASUVORK5CYII=';

const baseElement = document.querySelector('base') as HTMLBaseElement | null;
const baseUrl = baseElement?.getAttribute('href') ?? '';

// Webpack dynamic-chunk public path must track the served base URL.
declare var __webpack_public_path__: string;
__webpack_public_path__ = baseUrl;

const favIconNode = () => document.querySelector('link[rel="shortcut icon"]') as HTMLLinkElement | null;
const titleElement = () => document.querySelector('head title') as HTMLTitleElement | null;

const spaProps: ISinglePageApplicationProps = {
    baseUrl,
    apiBaseUrl: baseElement?.dataset.apiBase || '',
    getPageTitle: () => titleElement()?.textContent || '',
    setPageTitle: (newTitle: string) => {
        const el = titleElement();
        if (el && el.textContent !== newTitle) {
            el.textContent = newTitle;
        }
    },
    getFaviconUrl: () => favIconNode()?.getAttribute('href') || '',
    setFaviconUrl: (faviconUrl?: string) => {
        const node = favIconNode();
        if (node) {
            node.setAttribute('href', faviconUrl || DEFAULT_FAVICON_HREF);
        }
    },
    getVersion: (): VersionInfo => ({
        packageVersion: VERSION,
        commitHash: COMMITHASH,
        branch: BRANCH,
        packageVersionWithTag: VERSION_WITH_TAG,
    }),
};

// Check if running in Geotab integrated mode and configure before rendering.
if ((window as any).geotab) {
    const geotabManager = new GeotabManager();
    geotabManager.configureApplicationForIntegratedMode();
}

let container = document.getElementById('reactApp');
if (!container) {
    container = document.createElement('div');
    container.id = 'reactApp';
    document.body.prepend(container);
}

createRoot(container).render(<Application {...spaProps} />);
